import { titleCase } from '../titleCase.ts';
import type { ProcessKind } from './gates.ts';
import { holdersOf, instanceLinks, isWithin, planDisable, planEnable, treeLinks, wouldLoop, type ProcessStep, type ProjectProcess, type StepLink, type Tree } from './graph.ts';
import type { PhaseKey } from './phases.ts';
import { holdsList, startKey, type StepKind } from './schema.ts';
import { BUILDER_END } from './builder.ts';

/**
 * Changes to a project's process, as pure functions: each returns the new
 * process. The API runs the same plans against the database; the web uses
 * these to check a change before it is asked for and to say what it did.
 */

export type NewId = () => string;

export function counterIds(prefix = 's'): NewId {
  let n = 0;
  return () => `${prefix}${++n}`;
}

/** A step as the tree defines it, before it has an id. */
export function treeStep(tree: Tree, key: string, instance: string | null = null): Omit<ProcessStep, 'id' | 'parentId'> {
  const d = tree.node(key);
  if (!d) throw new Error(`No step ${key} in the tree`);
  if (d.kind === 'gate') {
    return { key, instance: null, kind: 'gate', title: d.title, state: 'not_started', days: 0, off: false, startedOn: null, doneOn: null };
  }
  return {
    key,
    instance,
    kind: d.kind,
    title: d.title,
    state: 'not_started',
    // An inspection is a visit: a day, unless the tree says otherwise.
    days: d.days ?? (d.kind === 'inspection' ? 1 : null),
    off: false,
    startedOn: null,
    doneOn: null,
  };
}

function withLinks(p: ProjectProcess, links: StepLink[]): ProjectProcess {
  const seen = new Set(p.links.map((l) => `${l.stepId}|${l.needsId}|${l.kind}`));
  const fresh = links.filter((l) => !seen.has(`${l.stepId}|${l.needsId}|${l.kind}`));
  return { ...p, links: [...p.links, ...fresh] };
}

/**
 * A fresh job: its start alone, with what is basic in its list, done as it is
 * set up. Everything else is added from what can come after it.
 */
export function configure(tree: Tree, kind: ProcessKind, newId: NewId): ProjectProcess {
  const start = startKey(tree.doc);
  const empty: ProjectProcess = { kind, steps: [], links: [] };
  if (!start) return empty;
  const steps: ProcessStep[] = [];
  const ids = new Map<string, string>();
  for (const key of planEnable(tree, empty, start).add) {
    const parent = tree.node(key)?.parent;
    const id = newId();
    ids.set(key, id);
    steps.push({ id, parentId: parent ? (ids.get(parent) ?? null) : null, ...treeStep(tree, key), ...(key === start ? { state: 'done' as const } : {}) });
  }
  const p: ProjectProcess = { ...empty, steps };
  return withLinks(p, treeLinks(tree, p));
}

/**
 * Leave a step of the tree out of this job (design study 07): it is never
 * offered again unless put back, and what comes after it is offered in its
 * place. Only a step the job does not have, and never the start.
 */
export function leaveOut(tree: Tree, p: ProjectProcess, key: string): ProjectProcess {
  const d = tree.node(key);
  if (!d || d.parent !== undefined) throw new Error('No such step in the tree.');
  if (key === startKey(tree.doc)) throw new Error(`${titleCase(d.title)} is where every job starts.`);
  if (p.steps.some((s) => s.key === key && s.instance === null && !s.off)) throw new Error(`${titleCase(d.title)} is in this job: take it out of the job first.`);
  const out = p.out ?? [];
  return out.includes(key) ? p : { ...p, out: [...out, key] };
}

/** Put a step left out back among what can come after the steps the job has. */
export function putBack(p: ProjectProcess, key: string): ProjectProcess {
  const out = p.out ?? [];
  if (!out.includes(key)) throw new Error('That step is not left out of this job.');
  return { ...p, out: out.filter((k) => k !== key) };
}

/**
 * Add a step of the tree, with its own list; one turned off and kept comes back as it was. `phase`: the phase it lands
 * in when that is not its own (its own phase closed, it lands late in the phase under way: graph `landingPhase`), set
 * on it and on its list.
 */
export function enable(tree: Tree, p: ProjectProcess, key: string, newId: NewId, opts: { phase?: PhaseKey } = {}): ProjectProcess {
  const plan = planEnable(tree, p, key);
  const steps = [...p.steps];
  const ids = new Map(steps.filter((s) => s.key && !s.instance).map((s) => [s.key!, s.id]));
  const touched = new Set<string>();
  const landed = opts.phase ? { phase: opts.phase } : {};
  for (const id of plan.restore) {
    const i = steps.findIndex((s) => s.id === id);
    steps[i] = { ...steps[i]!, off: false, ...landed };
    touched.add(id);
  }
  for (const k of plan.add) {
    const parent = tree.node(k)?.parent;
    const id = newId();
    ids.set(k, id);
    steps.push({ id, parentId: parent ? (ids.get(parent) ?? null) : null, ...treeStep(tree, k), ...landed });
    touched.add(id);
  }
  const next: ProjectProcess = {
    ...p,
    steps,
    // A step left out (or filed away when taken out) and added after all is in the job, no longer out of it.
    ...(p.out ? { out: p.out.filter((k) => k !== key && !plan.add.includes(k)) } : {}),
  };
  // Its place in the order: the tree's waits touching what came on. The named copies already on wait on what comes on
  // now, as they would have had it been on when they were added.
  const copies = next.steps.filter((s) => s.instance !== null && s.key !== null && !s.off && tree.node(s.key)?.repeatable);
  const fromTree = treeLinks(tree, next);
  const links = [...fromTree, ...copies.flatMap((c) => instanceLinks(tree, next, c.key!, c.instance!))];
  const fresh = links.filter((l) => touched.has(l.stepId) || touched.has(l.needsId));
  return withLinks(openUp(tree, next, touched, fresh, fromTree), fresh);
}

/**
 * Whether a step brings Project Complete, the job's special last step: the master draws it straight into the tree's end
 * (Closeout and Handover on NSB's tree; Jason, 2026-10-29: "those two steps, when added to the project, result in project
 * complete being added as the special last step"). A step drawn into it later on the master brings it too.
 */
export const bringsEnd = (tree: Tree, key: string): boolean => (tree.beforeOf.get(BUILDER_END) ?? []).includes(key);

/** After `keys` came on: Project Complete with them where one of them brings it and the job has not got it (`bringsEnd`). */
export function withEnd(tree: Tree, p: ProjectProcess, keys: readonly string[], newId: NewId): ProjectProcess {
  if (!tree.node(BUILDER_END) || !keys.some((k) => bringsEnd(tree, k))) return p;
  if (p.steps.some((s) => s.key === BUILDER_END && s.instance === null && !s.off)) return p;
  return enable(tree, p, BUILDER_END, newId);
}

/**
 * A step come on between a step and what it waited on: that wait is closed up, the step waiting on the new one instead,
 * as taking a step out closes the order up the other way. A job whose steps came on in any order (ticked
 * from their phases' lists, design study 31) then has the waits a job grown forward has. Only a wait the new step sits
 * between in the tree's order goes, and never one on a gate or inspection not yet passed (an add never lifts a hold).
 */
function openUp(tree: Tree, p: ProjectProcess, touched: ReadonlySet<string>, fresh: readonly StepLink[], current: readonly StepLink[]): ProjectProcess {
  const now = new Set(current.map((l) => `${l.stepId}|${l.needsId}`));
  const waiting = new Set(fresh.filter((l) => touched.has(l.needsId) && !touched.has(l.stepId)).map((l) => l.stepId));
  if (!waiting.size) return p;
  const byId = new Map(p.steps.map((s) => [s.id, s]));
  const added = [...touched].map((id) => byId.get(id)?.key).filter((k): k is string => Boolean(k));
  // Everything before a key in the tree's order, walking what comes before it.
  const memo = new Map<string, Set<string>>();
  const behind = (key: string): Set<string> => {
    const got = memo.get(key);
    if (got) return got;
    const out = new Set<string>();
    memo.set(key, out);
    const stack = [...(tree.beforeOf.get(key) ?? [])];
    while (stack.length) {
      const k = stack.pop()!;
      if (out.has(k)) continue;
      out.add(k);
      stack.push(...(tree.beforeOf.get(k) ?? []));
    }
    return out;
  };
  const links = p.links.filter((l) => {
    if (l.kind !== 'requires' || !waiting.has(l.stepId) || touched.has(l.needsId) || now.has(`${l.stepId}|${l.needsId}`)) return true;
    const need = byId.get(l.needsId);
    if (!need || need.off || need.key === null) return true;
    if ((need.kind === 'gate' || need.kind === 'inspection') && need.state !== 'done') return true;
    return !added.some((k) => behind(k).has(need.key!));
  });
  return links.length === p.links.length ? p : { ...p, links };
}

/** Add a named copy of a repeatable step: "Second round", with its own list. */
export function addInstance(tree: Tree, p: ProjectProcess, key: string, name: string, newId: NewId): ProjectProcess {
  const d = tree.node(key);
  if (!d?.repeatable) throw new Error(`${key} is not added by name`);
  const instance = name.trim();
  if (!instance) throw new Error('Give it a name');
  const same = p.steps.find((s) => s.key === key && s.instance === instance);
  if (same) throw new Error(same.off ? `There is already a ${instance}, turned off: turn it back on, or give this one another name` : `There is already a ${instance}`);
  const steps = [...p.steps];
  const rootId = newId();
  steps.push({ id: rootId, parentId: null, ...treeStep(tree, key, instance), title: instance });
  const walk = (parentKey: string, parentId: string) => {
    for (const c of tree.kids(parentKey)) {
      if (!c.basic?.includes(p.kind)) continue;
      const id = newId();
      steps.push({ id, parentId, ...treeStep(tree, c.key, instance) });
      walk(c.key, id);
    }
  };
  walk(key, rootId);
  const next: ProjectProcess = { ...p, steps };
  return withLinks(next, instanceLinks(tree, next, key, instance));
}

export interface NewProcessStep {
  title: string;
  /** A gate of the job's own holds back what comes after it as the tree's gates do (design study 12, own step A). */
  kind: StepKind;
  parentId?: string | null;
  /** What it waits on. */
  requires?: string[];
  /** What waits on it. */
  before?: string[];
  days?: number | null;
  /**
   * The phase it is in: a step of the job's own, the one it was added to (design study 12); a task, the one it is done
   * in, its step's or an earlier one (design study 31, slice 4: core `taskPhaseRefused` says which it may be). None: a
   * task is in its step's.
   */
  phase?: PhaseKey | null;
}

/**
 * A step the job grew itself: not from the tree. `phasesOf` reads the job's phases (core `projectPhases`) as it stands
 * with the step on, for the waits drawn to it, as `link` reads them.
 */
export function addStep(p: ProjectProcess, s: NewProcessStep, newId: NewId, phasesOf?: (p: ProjectProcess) => ReadonlyMap<string, PhaseKey>): ProjectProcess {
  const id = newId();
  const step: ProcessStep = {
    id,
    key: null,
    instance: null,
    kind: s.kind,
    title: s.title.trim(),
    parentId: s.parentId ?? null,
    state: 'not_started',
    // A gate takes no time and is a mark on the schedule, as a gate the tree places is (treeStep).
    days: s.kind === 'gate' ? 0 : (s.days ?? null),
    off: false,
    startedOn: null,
    doneOn: null,
    ...(s.phase ? { phase: s.phase } : {}),
  };
  let next: ProjectProcess = { ...p, steps: [...p.steps, step] };
  checkHolder(next, id, step.parentId);
  for (const r of s.requires ?? []) next = link(next, id, r, phasesOf?.(next));
  for (const b of s.before ?? []) next = link(next, b, id, phasesOf?.(next));
  return next;
}

/**
 * `stepId` waits on `needsId`. Refuses a loop, and a step tied to what holds it. `phases`, the job's (core
 * `projectPhases`), as `wouldLoop` reads them: a task done early is not after its step's start (design study 31, slice 4).
 */
export function link(p: ProjectProcess, stepId: string, needsId: string, phases?: ReadonlyMap<string, PhaseKey>): ProjectProcess {
  const live = new Map(p.steps.filter((s) => !s.off).map((s) => [s.id, s]));
  const a = live.get(stepId);
  const b = live.get(needsId);
  if (!a || !b) throw new Error('Both steps must be on this project.');
  if (stepId === needsId) throw new Error('A step cannot wait on itself.');
  if (isWithin(p, stepId, needsId) || isWithin(p, needsId, stepId)) throw new Error(`${isWithin(p, stepId, needsId) ? a.title : b.title} runs inside ${isWithin(p, stepId, needsId) ? b.title : a.title}: they are already in order.`);
  if (wouldLoop(p, stepId, needsId, phases)) throw new Error(`${titleCase(b.title)} already comes after ${titleCase(a.title)}: that would loop the order back on itself.`);
  if (p.links.some((l) => l.stepId === stepId && l.needsId === needsId)) return p;
  return { ...p, links: [...p.links, { stepId, needsId, kind: 'requires' }] };
}

export function unlink(p: ProjectProcess, stepId: string, needsId: string): ProjectProcess {
  return { ...p, links: p.links.filter((l) => !(l.stepId === stepId && l.needsId === needsId)) };
}

/** Only a step of work, a decision or a sign-off holds steps, and never what holds it. */
function checkHolder(p: ProjectProcess, id: string, parentId: string | null): void {
  if (parentId === null) return;
  const live = new Map(p.steps.filter((s) => !s.off).map((s) => [s.id, s]));
  const holder = live.get(parentId);
  const step = live.get(id)!;
  if (!holder) throw new Error('That step is not on this project.');
  if (parentId === id || holdersOf(p, parentId).includes(id)) throw new Error(`${titleCase(holder.title)} is inside ${titleCase(step.title)}: a step cannot hold what holds it.`);
  // As the tree's own lists: work holds steps, and a decision or a sign-off holds its own list too (schema.ts).
  if (!holdsList(holder.kind)) throw new Error(`${titleCase(holder.title)} is ${KIND_WORDS[holder.kind]}: only work, a decision or a sign-off holds a list.`);
}

const KIND_WORDS: Record<StepKind, string> = { work: 'a step', gate: 'a sign-off', milestone: 'a milestone', decision: 'a decision', inspection: 'an inspection' };

/**
 * Take a step out of a job: what is inside it goes too. A step of the tree is
 * filed away as one left out (Jason, 2026-10-07: "turning off a node that has
 * been added should be the same as filing it away, whatever state it was in
 * saves, but it has no impact on the project"): its rows kept, off, as they
 * were, and its key out of the job, so it is drawn as its dot and never
 * offered until added again, when it comes back as it was. A step of the
 * job's own is kept off only with history. What waited on it comes after what
 * it came after, where the job has it: the order closes up.
 */
export function disable(tree: Tree, p: ProjectProcess, id: string, hasHistory: (id: string) => boolean = () => false): ProjectProcess {
  const plan = planDisable(p, id, hasHistory);
  if (plan.blocked) throw new Error(plan.blocked);
  const top = p.steps.find((s) => s.id === id)!;
  const filed = top.key !== null && top.instance === null && !top.parentId && Boolean(tree.node(top.key));
  const off = new Set(plan.off);
  const keep = new Set(filed ? plan.off : plan.keep);
  const steps = p.steps.filter((s) => !off.has(s.id) || keep.has(s.id)).map((s) => (off.has(s.id) ? { ...s, off: true } : s));
  // Off, it holds nothing and waits on nothing; added again, its waits are the tree's again.
  const links = p.links.filter((l) => !off.has(l.stepId) && !off.has(l.needsId));
  const out = filed && !(p.out ?? []).includes(top.key!) ? [...(p.out ?? []), top.key!] : p.out;
  const next: ProjectProcess = { ...p, steps, links, ...(out ? { out } : {}) };
  const near = new Set(p.links.flatMap((l) => (off.has(l.stepId) || off.has(l.needsId) ? [l.stepId, l.needsId] : [])).filter((x) => !off.has(x)));
  return withLinks(next, treeLinks(tree, next).filter((l) => near.has(l.stepId) && near.has(l.needsId)));
}

/** A client's job becomes NSB's own, or the other way: its steps stay as they are, added and taken out by hand. */
export function setKind(p: ProjectProcess, kind: ProcessKind): ProjectProcess {
  return kind === p.kind ? p : { ...p, kind };
}

/** Mark a step's state; done gives it a day. */
export function setState(p: ProjectProcess, id: string, state: ProcessStep['state'], on: string): ProjectProcess {
  return {
    ...p,
    steps: p.steps.map((s) =>
      s.id !== id
        ? s
        : {
            ...s,
            state,
            startedOn: state === 'in_progress' || state === 'done' ? (s.startedOn ?? on) : s.startedOn,
            doneOn: state === 'done' ? (s.doneOn ?? on) : null,
          },
    ),
  };
}

export function rename(p: ProjectProcess, id: string, title: string): ProjectProcess {
  return { ...p, steps: p.steps.map((s) => (s.id === id ? { ...s, title } : s)) };
}

